import React, { useState, useEffect } from 'react';
import Layout from '../../components/ui/Layout';
import NavigationPanel from './components/NavigationPanel';
import ProviderVerification from './components/ProviderVerification';
import KYCReview from '../partner-analytics-hub/components/KYCReview';
import StaffDirectory from './components/StaffDirectory';
import UserControls from './components/UserControls';
import FinancialManagement from './components/FinancialManagement';
import { Toaster } from 'react-hot-toast';

const UserPartnerManagement = () => {
  const [activeSection, setActiveSection] = useState('provider-verification');

  // Mock data for service providers
  const [serviceProviders, setServiceProviders] = useState([
    {
      id: 1,
      name: 'TechFix Solutions',
      type: 'technician',
      businessName: 'TechFix Solutions Pvt Ltd',
      contactPerson: 'John Smith',
      email: 'john@techfix.com',
      phone: '+1-555-0123',
      status: 'pending',
      verificationStage: 'documents',
      documents: {
        businessLicense: { url: '/documents/license1.pdf', alt: 'Business license document for TechFix Solutions', status: 'uploaded' },
        taxCertificate: { url: '/documents/tax1.pdf', alt: 'Tax certificate for TechFix Solutions', status: 'uploaded' },
        insuranceProof: { url: '/documents/insurance1.pdf', alt: 'Insurance proof document for TechFix Solutions', status: 'pending' }
      },
      services: ['Computer Repair', 'Network Setup', 'Software Installation'],
      location: 'New York, NY',
      rating: null,
      createdAt: '2024-10-15',
      updatedAt: '2024-10-28'
    },
    {
      id: 2,
      name: 'FastDelivery Express',
      type: 'delivery',
      businessName: 'FastDelivery Express LLC',
      contactPerson: 'Sarah Johnson',
      email: 'sarah@fastdelivery.com',
      phone: '+1-555-0456',
      status: 'approved',
      verificationStage: 'completed',
      documents: {
        businessLicense: { url: '/documents/license2.pdf', alt: 'Business license for FastDelivery Express', status: 'verified' },
        taxCertificate: { url: '/documents/tax2.pdf', alt: 'Tax certificate for FastDelivery Express', status: 'verified' },
        vehicleRegistration: { url: '/documents/vehicle2.pdf', alt: 'Vehicle registration document', status: 'verified' }
      },
      services: ['Same Day Delivery', 'Express Shipping', 'Package Handling'],
      location: 'Los Angeles, CA',
      rating: 4.8,
      createdAt: '2024-09-20',
      updatedAt: '2024-10-25'
    },
    {
      id: 3,
      name: 'Gourmet Bistro',
      type: 'restaurant',
      businessName: 'Gourmet Bistro Inc',
      contactPerson: 'Marco Rodriguez',
      email: 'marco@gourmetbistro.com',
      phone: '+1-555-0789',
      status: 'rejected',
      verificationStage: 'compliance',
      documents: {
        businessLicense: { url: '/documents/license3.pdf', alt: 'Business license for Gourmet Bistro', status: 'verified' },
        healthPermit: { url: '/documents/health3.pdf', alt: 'Health permit for restaurant operations', status: 'rejected' },
        menuCertification: { url: '/documents/menu3.pdf', alt: 'Menu certification document', status: 'pending' }
      },
      services: ['Italian Cuisine', 'Catering', 'Takeout'],
      location: 'Chicago, IL',
      rating: null,
      rejectionReason: 'Health permit expired, menu certification incomplete',
      createdAt: '2024-10-01',
      updatedAt: '2024-10-27'
    }
  ]);

  // Mock data for KYC documents
  const [kycDocuments, setKycDocuments] = useState([
    {
      id: 1,
      userId: 101,
      userName: 'Alice Cooper',
      userType: 'customer',
      documentType: 'aadhaar',
      frontImage: { url: '/kyc/aadhaar-front-1.jpg', alt: 'Aadhaar card front side showing ID number and personal details' },
      backImage: { url: '/kyc/aadhaar-back-1.jpg', alt: 'Aadhaar card back side showing address information' },
      extractedData: {
        name: 'Alice Cooper',
        aadhaarNumber: '1234-5678-9012',
        address: '123 Main Street, New York, NY 10001',
        dateOfBirth: '1985-03-15'
      },
      status: 'pending',
      submittedAt: '2024-10-28 09:30:00',
      reviewedAt: null,
      reviewedBy: null,
      notes: ''
    },
    {
      id: 2,
      userId: 102,
      userName: 'Bob Wilson',
      userType: 'seller',
      documentType: 'aadhaar',
      frontImage: { url: '/kyc/aadhaar-front-2.jpg', alt: 'Aadhaar card front showing identification details and photo' },
      backImage: { url: '/kyc/aadhaar-back-2.jpg', alt: 'Aadhaar card back with complete address and QR code' },
      extractedData: {
        name: 'Bob Wilson',
        aadhaarNumber: '9876-5432-1098',
        address: '456 Oak Avenue, Los Angeles, CA 90210',
        dateOfBirth: '1990-07-22'
      },
      status: 'approved',
      submittedAt: '2024-10-27 14:45:00',
      reviewedAt: '2024-10-28 08:15:00',
      reviewedBy: 'Admin User',
      notes: 'All documents verified successfully'
    },
    {
      id: 3,
      userId: 103,
      userName: 'Carol Davis',
      userType: 'seller',
      documentType: 'aadhaar',
      frontImage: { url: '/kyc/aadhaar-front-3.jpg', alt: 'Aadhaar card front side with personal identification information' },
      backImage: { url: '/kyc/aadhaar-back-3.jpg', alt: 'Aadhaar card back side showing residential address details' },
      extractedData: {
        name: 'Carol Davis',
        aadhaarNumber: '5555-6666-7777',
        address: '789 Pine Street, Chicago, IL 60601',
        dateOfBirth: '1988-11-10'
      },
      status: 'rejected',
      submittedAt: '2024-10-26 16:20:00',
      reviewedAt: '2024-10-27 10:30:00',
      reviewedBy: 'Admin User',
      notes: 'Document quality too low, name mismatch with profile'
    }
  ]);

  // Mock data for staff
  const [staffMembers, setStaffMembers] = useState([
    {
      id: 1,
      name: 'David Kim',
      email: 'david.kim@company.com',
      role: 'Manager',
      department: 'Operations',
      merchantId: 'M001',
      merchantName: 'TechFix Solutions',
      status: 'active',
      permissions: ['view_orders', 'manage_inventory', 'customer_support'],
      phone: '+1-555-1111',
      hireDate: '2024-01-15',
      lastLogin: '2024-10-28 10:30:00',
      createdAt: '2024-01-15',
      updatedAt: '2024-10-28'
    },
    {
      id: 2,
      name: 'Emma Thompson',
      email: 'emma.thompson@company.com',
      role: 'Associate',
      department: 'Customer Service',
      merchantId: 'M002',
      merchantName: 'FastDelivery Express',
      status: 'pending',
      permissions: ['customer_support', 'view_orders'],
      phone: '+1-555-2222',
      hireDate: '2024-10-20',
      lastLogin: null,
      createdAt: '2024-10-20',
      updatedAt: '2024-10-28'
    },
    {
      id: 3,
      name: 'Michael Chen',
      email: 'michael.chen@company.com',
      role: 'Supervisor',
      department: 'Quality Control',
      merchantId: 'M003',
      merchantName: 'Gourmet Bistro',
      status: 'inactive',
      permissions: ['view_orders', 'quality_check', 'inventory_management'],
      phone: '+1-555-3333',
      hireDate: '2024-02-10',
      lastLogin: '2024-10-25 15:45:00',
      createdAt: '2024-02-10',
      updatedAt: '2024-10-27'
    }
  ]);

  // Mock data for users
  const [users, setUsers] = useState([
    {
      id: 101,
      name: 'Alice Cooper',
      email: 'alice@email.com',
      phone: '+1-555-0001',
      userType: 'customer',
      status: 'active',
      registrationDate: '2024-03-15',
      lastLogin: '2024-10-28 09:15:00',
      totalOrders: 25,
      totalSpent: 1250.00,
      kycStatus: 'pending',
      location: 'New York, NY'
    },
    {
      id: 102,
      name: 'Bob Wilson',
      email: 'bob@email.com',
      phone: '+1-555-0002',
      userType: 'seller',
      status: 'active',
      registrationDate: '2024-02-20',
      lastLogin: '2024-10-28 08:30:00',
      totalOrders: 150,
      totalRevenue: 15500.00,
      kycStatus: 'approved',
      location: 'Los Angeles, CA'
    },
    {
      id: 103,
      name: 'Carol Davis',
      email: 'carol@email.com',
      phone: '+1-555-0003',
      userType: 'seller',
      status: 'suspended',
      registrationDate: '2024-01-10',
      lastLogin: '2024-10-20 14:20:00',
      totalOrders: 75,
      totalRevenue: 8200.00,
      kycStatus: 'rejected',
      location: 'Chicago, IL',
      suspensionReason: 'Multiple customer complaints'
    }
  ]);

  // Mock data for subscriptions and financial management
  const [subscriptions, setSubscriptions] = useState([
    {
      id: 1,
      merchantId: 'M001',
      merchantName: 'TechFix Solutions',
      plan: 'Professional',
      monthlyFee: 99.99,
      commissionRate: 2.5,
      status: 'active',
      startDate: '2024-01-15',
      nextBilling: '2024-11-15',
      totalRevenue: 15800.00,
      pendingPayout: 395.00,
      lastPayout: '2024-10-01',
      payoutSchedule: 'monthly'
    },
    {
      id: 2,
      merchantId: 'M002',
      merchantName: 'FastDelivery Express',
      plan: 'Enterprise',
      monthlyFee: 199.99,
      commissionRate: 1.8,
      status: 'active',
      startDate: '2024-02-01',
      nextBilling: '2024-11-01',
      totalRevenue: 28500.00,
      pendingPayout: 513.00,
      lastPayout: '2024-10-01',
      payoutSchedule: 'bi-weekly'
    },
    {
      id: 3,
      merchantId: 'M003',
      merchantName: 'Gourmet Bistro',
      plan: 'Basic',
      monthlyFee: 49.99,
      commissionRate: 3.2,
      status: 'suspended',
      startDate: '2024-03-10',
      nextBilling: null,
      totalRevenue: 5200.00,
      pendingPayout: 0.00,
      lastPayout: '2024-09-01',
      payoutSchedule: 'monthly'
    }
  ]);

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'provider-verification':
        return (
          <ProviderVerification
            providers={serviceProviders}
            setProviders={setServiceProviders}
          />
        );
      case 'kyc-review':
        return (
          <KYCReview
            kycDocuments={kycDocuments}
            setKycDocuments={setKycDocuments}
          />
        );
      case 'staff-directory':
        return (
          <StaffDirectory
            staffMembers={staffMembers}
            setStaffMembers={setStaffMembers}
            providers={serviceProviders}
          />
        );
      case 'user-controls':
        return (
          <UserControls
            users={users}
            setUsers={setUsers}
          />
        );
      case 'financial-management':
        return (
          <FinancialManagement
            subscriptions={subscriptions}
            setSubscriptions={setSubscriptions}
          />
        );
      default:
        return (
          <ProviderVerification
            providers={serviceProviders}
            setProviders={setServiceProviders}
          />
        );
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">User & Partner Management</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Comprehensive administrative center for managing marketplace participants, verification, and financial operations
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex h-[calc(100vh-120px)]">
          {/* Navigation Panel */}
          <NavigationPanel
            activeSection={activeSection}
            setActiveSection={setActiveSection}
            providers={serviceProviders}
            kycDocuments={kycDocuments}
            staffMembers={staffMembers}
            users={users}
            subscriptions={subscriptions}
          />

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            {renderActiveSection()}
          </div>
        </div>

        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'hsl(var(--card))',
              color: 'hsl(var(--foreground))',
              border: '1px solid hsl(var(--border))',
            },
            success: {
              iconTheme: {
                primary: 'hsl(var(--primary))',
                secondary: 'hsl(var(--primary-foreground))',
              },
            },
            error: {
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
          }}
        />
      </div>
    </Layout>
  );
};

export default UserPartnerManagement;